import { Tooltip as BaseTooltip } from '@base-ui/react/tooltip';
import React, { useState } from 'react';

import type { IconName, IconSize } from '../../assets/Icon/Icon';
import type { DynamicTextPropType } from '../../text/DynamicText';
import type { AlignStackProps } from '../../utilities/AlignStack/AlignStack';
import type { ColorProps } from '../../utilities/shared/Color';
import { Icon } from '../../assets/Icon/Icon';
import {
  slideDownAndFade,
  slideLeftAndFade,
  slideRightAndFade,
  slideUpAndFade,
} from '../../common/animations';
import { type VariantProp, fade, palette } from '../../common/colors';
import { VisuallyHidden } from '../../common/visually_hidden';
import { zIndexes } from '../../common/z_indexes';
import { selectors } from '../../controls/shared/styles';
import { colors, darkThemeSelector, keyframes, shadows, styled } from '../../stitches.config';
import { DynamicText } from '../../text/DynamicText';
import { Small } from '../../text/Small';
import { AlignStack } from '../../utilities/AlignStack/AlignStack';
import { useCanHover } from '../../utilities/getCanHover';

const TooltipTrigger = styled(BaseTooltip.Trigger, {
  [selectors.focus]: {
    outline: 'none',
  },
});
TooltipTrigger.displayName = 'TooltipTrigger';

const tooltipGlowLight = keyframes({
  '0%, 100%': {
    boxShadow: `
      ${shadows.overlayAppLight},
      0 0 4px 0 ${fade(palette.brand600, 0.15)}
    `,
  },
  '50%': {
    boxShadow: `
      ${shadows.overlayAppLight},
      0 0 12px 0 ${fade(palette.brand600, 0.4)}
    `,
  },
});

const tooltipGlowDark = keyframes({
  '0%, 100%': {
    boxShadow: `
      ${shadows.overlayAppDark},
      0 0 4px 0 ${fade(palette.brand600, 0.15)}
    `,
  },
  '50%': {
    boxShadow: `
      ${shadows.overlayAppDark},
      0 0 12px 0 ${fade(palette.brand600, 0.4)}
    `,
  },
});

export const TooltipContainer = styled('div', Small, {
  display: 'inline-flex',
  width: 'fit-content',
  maxWidth: '$280',
  backgroundColor: colors.bgApplicationLight,
  boxShadow: shadows.overlayLight,
  borderRadius: '$8',
  color: colors.gray700,
  pointerEvents: 'none',
  userSelect: 'none',

  [darkThemeSelector]: {
    color: colors.gray50,
    backgroundColor: colors.bgApplicationDark,
    boxShadow: shadows.overlayDark,
  },

  [selectors.focus]: {
    outline: 'none',
  },

  variants: {
    hideOnDesktop: {
      true: {
        '@desktop': {
          display: 'none',
        },
      },
      false: {},
    },
    pulse: {
      true: {
        animation: `${tooltipGlowLight} 1.5s ease-in-out infinite`,

        [darkThemeSelector]: {
          animation: `${tooltipGlowDark} 1.5s ease-in-out infinite`,
        },
      },
      false: {},
    },
    tight: {
      true: {},
      false: {
        padding: '$4 $6',
      },
    },
  },
});

const TooltipPositioner = styled(BaseTooltip.Positioner, {
  zIndex: zIndexes.tooltip,
});
TooltipPositioner.displayName = 'TooltipPositioner';

const TooltipPopup = styled(BaseTooltip.Popup, {
  display: 'contents',
  pointerEvents: 'none',
  userSelect: 'none',

  '@media (prefers-reduced-motion: no-preference)': {
    animationDuration: '150ms',
    animationTimingFunction: 'ease-out',
    animationFillMode: 'forwards',
    willChange: 'transform, opacity',

    '&[data-open]': {
      '&[data-side="top"]': { animationName: slideUpAndFade },
      '&[data-side="right"]': { animationName: slideLeftAndFade },
      '&[data-side="bottom"]': { animationName: slideDownAndFade },
      '&[data-side="left"]': { animationName: slideRightAndFade },
    },
  },
});
TooltipPopup.displayName = 'TooltipPopup';

export type TooltipSide = 'top' | 'right' | 'bottom' | 'left';

export interface TooltipProps {
  /**
   * Set whether the tooltip should wrap its children or be mounted to its children.
   */
  asChild?: boolean;
  /**
   * How the tooltip aligns to its trigger, defaults to center.
   */
  align?: 'center' | 'end' | 'start';
  /**
   * Any child will be set as the trigger for the tooltip.
   */
  children?: React.ReactNode;
  /**
   * The distance in pixels from the trigger, defaults to 6.
   */
  collisionPadding?: number;
  /**
   * Pass in any content to display inside the tooltip.
   */
  contents?: React.ReactNode;
  /**
   * The delay in milliseconds before the tooltip is shown, defaults to 100.
   */
  delay?: number;
  /**
   * Hides the tooltip for desktop users.
   */
  hideOnDesktop?: boolean;
  /**
   * If true, the tooltip will pulse when it is open.
   */
  pulse?: boolean;
  /**
   * The preferred side of the trigger to render against when open.
   */
  side?: TooltipSide;
  /**
   * The distance in pixels from the trigger, defaults to 6.
   */
  sideOffset?: number;
  /**
   * Treats the tooltip as a toggle, opening it (and closing it) when the trigger is clicked,
   * instead of on hover.
   */
  toggle?: boolean;
  /**
   * If true, remove padding from the tooltip container.
   */
  tight?: boolean;
  /**
   * If provided, controls the open state of the tooltip.
   */
  isOpen?: boolean;
  /**
   * When true, the tooltip will not open.
   */
  disabled?: boolean;
}

export function Tooltip({
  align,
  asChild = true,
  children,
  collisionPadding = 12,
  contents,
  delay = 100,
  hideOnDesktop,
  pulse = false,
  side,
  sideOffset = 6,
  toggle,
  tight = false,
  isOpen,
  disabled = false,
}: TooltipProps) {
  const isControlled = isOpen !== undefined;
  const [open, setOpen] = useState(false);
  const canHover = useCanHover();

  // Set toggle to true by default for mobile viewports, false for devices without hover media
  const shouldToggle = toggle ?? !canHover;

  const openProps: Pick<BaseTooltip.Root.Props, 'open' | 'onOpenChange'> = {};

  if (isControlled) {
    openProps.open = isOpen;
  } else if (shouldToggle) {
    openProps.open = open;
    openProps.onOpenChange = (_nextOpen, eventDetails) => {
      if (eventDetails.reason === 'outside-press' || eventDetails.reason === 'escape-key') {
        setOpen(false);
      }
    };
  }

  return (
    <BaseTooltip.Root disabled={disabled} {...openProps}>
      <TooltipTrigger
        closeOnClick={!shouldToggle}
        delay={delay}
        onClick={shouldToggle && !isControlled ? () => setOpen((value) => !value) : undefined}
        render={
          asChild && React.isValidElement(children) ? (children as React.ReactElement) : undefined
        }
        type={asChild ? undefined : 'button'}
      >
        {asChild ? null : children}
      </TooltipTrigger>
      <BaseTooltip.Portal>
        <TooltipPositioner
          align={align}
          collisionPadding={collisionPadding}
          side={side}
          sideOffset={sideOffset}
        >
          <TooltipPopup>
            {contents ? (
              <TooltipContainer hideOnDesktop={hideOnDesktop} pulse={pulse} tight={tight}>
                {contents}
              </TooltipContainer>
            ) : null}
          </TooltipPopup>
        </TooltipPositioner>
      </BaseTooltip.Portal>
    </BaseTooltip.Root>
  );
}

const IconTooltipIcon = styled(Icon);
IconTooltipIcon.displayName = 'IconTooltipIcon';

const IconTooltipContainer = styled('div', {
  display: 'flex',
});
IconTooltipContainer.displayName = 'IconTooltipContainer';

export type IconTooltipProps = TooltipProps & {
  /**
   * The icon to display in the tooltip.
   */
  icon?: IconName;
  /**
   * Whether the tooltip is internal.
   */
  internal?: boolean;
  /**
   * The size of the icon.
   */
  size?: IconSize;
  /**
   * The variant of the icon.
   */
  variant?: VariantProp | 'inherit';
  /**
   * The color of the icon
   */
  color?: ColorProps;
};

export function IconTooltip({
  internal = false,
  icon = 'question',
  size = 14,
  variant = 'neutral',
  color,
  ...remaining
}: IconTooltipProps) {
  const { contents } = remaining;

  return (
    <Tooltip {...remaining}>
      <IconTooltipContainer>
        <IconTooltipIcon
          icon={icon}
          internal={internal}
          size={size}
          variant={variant}
          color={color}
        />
        <VisuallyHidden>{contents}</VisuallyHidden>
      </IconTooltipContainer>
    </Tooltip>
  );
}

export type LabelTooltipProps = Omit<IconTooltipProps, 'variant'> & {
  /**
   * The context of the text, whether it's a heading or body text.
   */
  context?: 'body' | 'heading';
  /**
   * The preset of the text, whether it's a caption or body text.
   */
  preset?: DynamicTextPropType;
  /**
   * Whether the text is internal.
   */
  internal?: boolean;
  /**
   * Whether the text should be truncated.
   */
  truncate?: boolean;
  /**
   * The width of the text.
   */
  width?: AlignStackProps['width'];
  /**
   * The variant of the tooltip and icon.
   */
  variant?: VariantProp;
};

export function LabelTooltip({
  contents,
  context = 'body',
  children,
  icon = 'question',
  internal = false,
  size = 14,
  preset = 'body',
  truncate = true,
  variant = 'neutral',
  width = 'fit-content',
  ...remaining
}: LabelTooltipProps) {
  const renderLabel = (
    <AlignStack
      align="center"
      direction="row"
      gap={4}
      preset={preset}
      width={width}
      end={
        contents ? (
          <IconTooltipIcon icon={icon} internal={internal} size={size} variant={variant} />
        ) : undefined
      }
    >
      <DynamicText
        context={context}
        type={preset}
        variant={variant}
        truncate={truncate}
        internal={internal}
      >
        {children}
      </DynamicText>
    </AlignStack>
  );

  if (contents) {
    return (
      <Tooltip contents={contents} {...remaining}>
        {renderLabel}
      </Tooltip>
    );
  }

  return renderLabel;
}
